<template>
  <HrPageLayout
    title="Payroll ledger vs employee register"
    :breadcrumb-items="breadcrumbItems"
  >
    <div class="pcr-content">
      <div class="alert alert-info hr-mb-3">
        <h6 class="alert-heading hr-mb-2">How to use the Payroll Reconciliation Tool</h6>
        <ul class="mb-0 ps-3">
          <li><strong>1. Upload Files:</strong> In the section below, upload your Payroll ledger, Pension files, and Gratuity report.</li>
          <li><strong>2. Analyze:</strong> After uploads and pre-validation, click the <strong>"Load register & analyze"</strong> button to process the data.</li>
          <li><strong>3. Load & Save Reports:</strong> View previous snapshots in the <strong>"Saved reports"</strong> tab, or click <strong>"Save report"</strong> after a successful analysis to keep a record.</li>
        </ul>
      </div>
      <div class="filters-card hr-mb-3">
        <p class="text-muted hr-small hr-mb-2">
          <strong>Spending entities</strong> are derived from <strong>Employee Register departments</strong> — not from a separate payroll column.
        </p>
        <div class="filters-grid">
          <div class="filter-group">
            <label class="filter-label">Month</label>
            <select v-model.number="periodMonth" class="filter-select" :disabled="periodFinalized" @change="onPeriodChange">
              <option v-for="m in monthOptions" :key="'m-' + m.value" :value="m.value">{{ m.label }}</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Year</label>
            <select v-model.number="periodYear" class="filter-select" :disabled="periodFinalized" @change="onPeriodChange">
              <option v-for="y in yearOptions" :key="'y-' + y" :value="y">{{ y }}</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Register scope</label>
            <select v-model="registerScope" class="filter-select">
              <option value="active">Active employees only</option>
              <option value="all">All employees (any status)</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">NITA per employee (KES)</label>
            <input v-model.number="nitaPerEmployee" type="number" min="0" step="0.01" class="filter-input" :disabled="periodFinalized || !canManage">
          </div>
          <div class="filter-group align-end">
            <button type="button" class="hr-btn hr-btn-outline-primary hr-btn-sm" :disabled="!canManage || periodFinalized || savingNita" @click="saveNitaConfig">
              {{ savingNita ? "Saving…" : "Save NITA" }}
            </button>
          </div>
        </div>

        <div v-if="periodFinalized" class="alert alert-info hr-mt-2 hr-mb-0">
          This month is <strong>finalized</strong>. Uploads and analysis are locked.
          <button v-if="canReopen" type="button" class="hr-btn hr-btn-warning hr-btn-sm hr-ms-2" @click="reopenMonth">Reopen month</button>
        </div>

        <div class="upload-grid hr-mt-3">
          <div class="upload-block">
            <label class="filter-label">Payroll ledger</label>
            <input ref="fileInput" type="file" accept=".csv,.xlsx,.xls" class="filter-input" :disabled="periodFinalized || uploadingAll || !!uploadStatus.payroll" @change="stageFile($event, 'payroll')">
            <span v-if="uploadStatus.payroll" class="hr-small text-success">✓ {{ uploadStatus.payroll }}</span>
            <span v-else-if="stagedFiles.payroll" class="hr-small text-muted">Staged: {{ stagedFiles.payroll.name }}</span>
          </div>
          <div v-for="code in [16, 32, 33, 101]" :key="'pen-' + code" class="upload-block">
            <label class="filter-label">Pension file (code {{ code }})</label>
            <input type="file" accept=".csv,.xlsx,.xls" class="filter-input" :disabled="periodFinalized || uploadingAll || !!uploadStatus['pension_' + code]" @change="stageFile($event, 'pension_' + code)">
            <span v-if="uploadStatus['pension_' + code]" class="hr-small text-success">✓ {{ uploadStatus['pension_' + code] }}</span>
            <span v-else-if="stagedFiles['pension_' + code]" class="hr-small text-muted">Staged: {{ stagedFiles['pension_' + code].name }}</span>
          </div>
          <div class="upload-block">
            <label class="filter-label">Gratuity report</label>
            <input type="file" accept=".csv,.xlsx,.xls" class="filter-input" :disabled="periodFinalized || uploadingAll || !!uploadStatus.gratuity" @change="stageFile($event, 'gratuity')">
            <span v-if="uploadStatus.gratuity" class="hr-small text-success">✓ {{ uploadStatus.gratuity }}</span>
            <span v-else-if="stagedFiles.gratuity" class="hr-small text-muted">Staged: {{ stagedFiles.gratuity.name }}</span>
          </div>
        </div>

        <div v-if="Object.keys(stagedFiles).length > 0" class="hr-mt-3">
          <button type="button" class="hr-btn hr-btn-primary hr-btn-sm" :disabled="uploadingAll" @click="uploadAllStagedFiles">
            {{ uploadingAll ? 'Uploading...' : 'Initiate Upload of Staged Files' }}
          </button>
          
          <div v-if="uploadingAll || uploadProgress > 0" class="progress hr-mt-2" style="height: 10px;">
            <div class="progress-bar progress-bar-striped progress-bar-animated bg-success" role="progressbar" :style="{ width: uploadProgress + '%' }" :aria-valuenow="uploadProgress" aria-valuemin="0" aria-valuemax="100"></div>
          </div>
        </div>
        
        <div v-if="uploadErrors && uploadErrors.length > 0" class="alert alert-danger hr-mt-2">
          <strong>Upload errors:</strong>
          <ul class="mb-0">
            <li v-for="(err, idx) in uploadErrors" :key="idx">{{ err }}</li>
          </ul>
        </div>

        <div v-if="preValidation" class="validation-panel hr-mt-3">
          <h6 class="hr-mb-2">Pre-analysis checklist</h6>
          <ul class="validation-list">
            <li :class="preValidation.checks.payroll ? 'ok' : 'fail'">Payroll file uploaded</li>
            <li :class="preValidation.checks.register ? 'ok' : 'fail'">Employee register available</li>
            <li :class="pensionFilesReady ? 'ok' : 'fail'">Four pension files uploaded</li>
            <li :class="preValidation.checks.gratuity ? 'ok' : 'fail'">Gratuity file uploaded</li>
            <li :class="preValidation.checks.nita ? 'ok' : 'fail'">NITA amount configured</li>
          </ul>
          <p v-if="preValidation.errors.length" class="text-danger hr-small">{{ preValidation.errors.join(" ") }}</p>
        </div>

        <div class="d-flex flex-wrap gap-2 hr-mt-3">
          <button type="button" class="hr-btn hr-btn-primary" :disabled="loading || !canRunAnalysis" @click="runAnalysis">
            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
            Load register &amp; analyze
          </button>
          <button type="button" class="hr-btn hr-btn-outline-secondary" :disabled="!analysisDone" @click="clearAll">Clear</button>
          <button type="button" class="hr-btn hr-btn-success" :disabled="!analysisDone || savingReport" @click="openSaveReport">
            {{ savingReport ? "Saving…" : "Save report" }}
          </button>
          <button v-if="canFinalize && analysisDone && !periodFinalized" type="button" class="hr-btn hr-btn-warning" @click="finalizeMonth">Finalize month</button>
          <button v-if="analysisDone" type="button" class="hr-btn hr-btn-outline-success hr-btn-sm" @click="exportPdfSummary">Export PDF</button>
          <button v-if="analysisDone" type="button" class="hr-btn hr-btn-outline-success hr-btn-sm" @click="exportExcelWorkbook">Export Excel</button>
        </div>

        <div v-if="headers.length" v-show="false" class="hr-mt-3 pcr-mapping">
          <h6 class="hr-mb-2">Column mapping (payroll ledger)</h6>
          <div class="mapping-grid">
            <div class="mapping-row">
              <span>Staff / ID</span>
              <select v-model="mapId" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'id-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
            <div class="mapping-row">
              <span>Department (CSV — for mismatch checks)</span>
              <select v-model="mapDept" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'dept-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
            <div class="mapping-row">
              <span>Pay amount</span>
              <select v-model="mapPay" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'pay-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
            <div class="mapping-row">
              <span>Name (optional)</span>
              <select v-model="mapName" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'nm-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
          </div>
          <p v-if="detectedEarnings.length || detectedDeductions.length" class="hr-small text-muted hr-mt-2">
            Detected {{ detectedEarnings.length }} earning code(s), {{ detectedDeductions.length }} deduction code(s) — saved to template catalog on analyze.
          </p>
        </div>
      </div>

      <div v-if="errorMsg" class="alert alert-warning">{{ errorMsg }}</div>

      <div v-if="saveReportMessage" class="alert" :class="saveReportError ? 'alert-danger' : 'alert-success'">{{ saveReportMessage }}</div>
      <div v-if="analysisDone && activeTab !== 'saved'" class="hr-mb-2">
        <button type="button" class="hr-btn hr-btn-outline-secondary hr-btn-sm" @click="activeTab = 'saved'">
          Back to saved reports
        </button>
      </div>

      <ul class="nav nav-tabs hr-mb-3">
        <li v-for="t in tabs" :key="t.id" class="nav-item">
          <button
            type="button"
            class="nav-link"
            :class="{ active: activeTab === t.id, disabled: t.requiresAnalysis && !analysisDone }"
            :disabled="t.requiresAnalysis && !analysisDone"
            @click="onTabClick(t.id)"
          >
            {{ t.label }}
          </button>
        </li>
      </ul>

      <div v-show="activeTab === 'saved'" class="table-card hr-mb-3">
        <p class="text-muted hr-small">
          Saved reconciliation snapshots (summary, spending entities, wage bill, matched / missing lists). Run <strong>Save report</strong> after analysis to store a copy.
        </p>
        <button
          type="button"
          class="hr-btn hr-btn-outline-secondary hr-btn-sm hr-mb-2"
          :disabled="savedReportsLoading"
          @click="loadSavedReports"
        >
          Refresh list
        </button>
        <div class="table-responsive">
          <table class="table table-bordered table-sm">
            <thead>
              <tr>
                <th>Saved at</th>
                <th>Period</th>
                <th>Title</th>
                <th>Employees</th>
                <th>Gross payroll</th>
                <th>Variance</th>
                <th>Register scope</th>
                <th>Saved by</th>
                <th style="width: 200px">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="savedReportsLoading">
                <td colspan="9" class="text-muted">Loading…</td>
              </tr>
              <tr v-else-if="!savedReports.length">
                <td colspan="9" class="text-muted">No saved reports yet.</td>
              </tr>
              <tr v-for="r in savedReports" :key="'sr-' + r.id">
                <td>{{ r.created_at }}</td>
                <td>{{ formatReportPeriod(r) }}</td>
                <td>{{ r.title }}</td>
                <td>{{ r.employee_count != null ? r.employee_count : "—" }}</td>
                <td>{{ r.gross_payroll != null ? formatMoney(r.gross_payroll) : "—" }}</td>
                <td>{{ r.variance_total != null ? formatMoney(r.variance_total) : "—" }}</td>
                <td>{{ r.register_scope }}</td>
                <td>{{ r.created_by_username || "—" }}</td>
                <td>
                  <button type="button" class="hr-btn hr-btn-primary hr-btn-sm" @click="loadSavedReport(r.id)">Load</button>
                  <button type="button" class="hr-btn hr-btn-outline-danger hr-btn-sm hr-ms-1" @click="deleteSavedReport(r.id)">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <template v-if="analysisDone">

        <div v-show="activeTab === 'summary'" class="table-card">
          <div class="d-flex flex-wrap gap-2 hr-mb-3">
            <button type="button" class="hr-btn hr-btn-success hr-btn-sm" @click="exportSummaryCsv">Download summary CSV</button>
          </div>
          <div class="row g-3 hr-mb-3">
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value">{{ summary.csvRowCount }}</div>
                <div class="pcr-stat-label">Rows in CSV</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value">{{ summary.registerCount }}</div>
                <div class="pcr-stat-label">Employees in register (scope)</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-success">{{ summary.matchedCount }}</div>
                <div class="pcr-stat-label">Matched (CSV ↔ register)</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-warning">{{ summary.csvOnlyCount }}</div>
                <div class="pcr-stat-label">In CSV, not in register</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-danger">{{ summary.registerOnlyCount }}</div>
                <div class="pcr-stat-label">Missing from payroll upload</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value">{{ formatMoney(summary.grossPay || summary.totalCsvPay) }}</div>
                <div class="pcr-stat-label">Gross payroll</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-warning">{{ summary.duplicateCount || 0 }}</div>
                <div class="pcr-stat-label">Duplicate payroll IDs</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-danger">{{ summary.inactivePaidCount || 0 }}</div>
                <div class="pcr-stat-label">Inactive but paid</div>
              </div>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'executive'" class="table-card">
          <h6>Spending Entities Summary Report — {{ periodLabel }}</h6>
          <div class="row g-3 hr-mb-3" v-if="executiveSummary">
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value">{{ formatMoney(executiveSummary.totalPayrollCost) }}</div><div class="pcr-stat-label">Total payroll</div></div></div>
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value">{{ formatMoney(executiveSummary.totalPensionCost) }}</div><div class="pcr-stat-label">Total pension (902)</div></div></div>
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value">{{ formatMoney(executiveSummary.totalGratuityCost) }}</div><div class="pcr-stat-label">Total gratuity</div></div></div>
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value">{{ formatMoney(executiveSummary.totalNitaCost) }}</div><div class="pcr-stat-label">Expected NITA</div></div></div>
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value">{{ executiveSummary.employeeCount }}</div><div class="pcr-stat-label">Employees matched</div></div></div>
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value text-warning">{{ formatMoney(executiveSummary.totalVariance) }}</div><div class="pcr-stat-label">Total variance</div></div></div>
          </div>
          <h6 class="hr-mt-3">Spending entity summary (register departments)</h6>
          <div class="table-responsive">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Department</th><th>Employees</th><th>Payroll</th><th>Pension</th><th>NITA</th><th>Total cost</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in (executiveSummary && executiveSummary.spendingEntitySummary) || []" :key="'ex-' + i">
                  <td>{{ r.department }}</td><td>{{ r.employeeCount }}</td>
                  <td>{{ formatMoney(r.payrollCost) }}</td><td>{{ formatMoney(r.pensionCost) }}</td>
                  <td>{{ formatMoney(r.nitaCost) }}</td><td>{{ formatMoney(r.totalCost) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'pension'" class="table-card">
          <p class="text-muted hr-small">Mandatory pension reconciliation: payroll deduction <strong>902</strong> vs pension file <strong>EDCode 902</strong>. Voluntary/AVC excluded.</p>
          <div class="row g-2 hr-mb-3" v-if="pensionRecon && pensionRecon.totals">
            <div class="col-md-3">Payroll 902: {{ formatMoney(pensionRecon.totals.payrollPension902) }}</div>
            <div class="col-md-3">Pension files: {{ formatMoney(pensionRecon.totals.pensionFiles902) }}</div>
            <div class="col-md-3">Variance: {{ formatMoney(pensionRecon.totals.variance) }}</div>
            <div class="col-md-3">Mismatches: {{ pensionRecon.totals.varianceCount }}</div>
          </div>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('pension')">Download CSV</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Name</th><th>Department</th><th>Payroll 902</th><th>Pension file</th><th>Variance</th><th>Status</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in (pensionRecon && pensionRecon.variances) || []" :key="'pv-' + i">
                  <td>{{ displayId(r.empNo) }}</td><td>{{ r.name }}</td><td>{{ r.department }}</td>
                  <td>{{ formatMoney(r.payrollPension) }}</td><td>{{ formatMoney(r.pensionFile) }}</td>
                  <td>{{ formatMoney(r.variance) }}</td><td>{{ r.status }}</td>
                </tr>
                <tr v-if="!(pensionRecon && pensionRecon.variances && pensionRecon.variances.length)"><td colspan="7" class="text-muted">No pension variances.</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'gratuity'" class="table-card">
          <p class="text-muted hr-small">Gratuity upload vs register/payroll. Total uploaded: {{ formatMoney(gratuityRecon && gratuityRecon.uploadedTotal) }}</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('gratuity')">Download CSV</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>ID</th><th>Name</th><th>Department</th><th>Amount</th><th>In register</th><th>In payroll</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in (gratuityRecon && gratuityRecon.uploadedRows) || []" :key="'gr-' + i">
                  <td>{{ displayId(r.rawId) }}</td><td>{{ r.name }}</td><td>{{ r.department }}</td>
                  <td>{{ formatMoney(r.amount) }}</td><td>{{ r.inRegister ? "Yes" : "No" }}</td><td>{{ r.inPayroll ? "Yes" : "No" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'nita'" class="table-card">
          <div class="row g-3">
            <div class="col-md-3"><strong>Rate:</strong> {{ formatMoney(nitaPerEmployee) }} / employee</div>
            <div class="col-md-3"><strong>Count:</strong> {{ summary.employeeCount || 0 }}</div>
            <div class="col-md-3"><strong>Expected:</strong> {{ formatMoney(summary.nitaExpected) }}</div>
            <div class="col-md-3"><strong>Actual (ledger):</strong> {{ formatMoney(summary.nitaActual) }}</div>
            <div class="col-md-3"><strong>Variance:</strong> {{ formatMoney(summary.nitaVariance) }}</div>
          </div>
        </div>

        <div v-show="activeTab === 'variances'" class="table-card">
          <h6>Department variances ({{ deptVariances.length }})</h6>
          <div class="table-responsive hr-mb-3" style="max-height: 240px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Name</th><th>Register dept</th><th>CSV dept</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in deptVariances" :key="'dv-' + i">
                  <td>{{ displayId(r.empNo) }}</td><td>{{ r.regName }}</td><td>{{ r.regDept }}</td><td>{{ r.csvDept }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h6>Designation variances ({{ designationVariances.length }})</h6>
          <div class="table-responsive hr-mb-3" style="max-height: 200px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Register</th><th>CSV</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in designationVariances" :key="'des-' + i">
                  <td>{{ displayId(r.empNo) }}</td><td>{{ r.regDesignation }}</td><td>{{ r.csvDesignation }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h6>Job group variances ({{ jobGroupVariances.length }})</h6>
          <div class="table-responsive hr-mb-3" style="max-height: 200px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Register</th><th>CSV</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in jobGroupVariances" :key="'jg-' + i">
                  <td>{{ displayId(r.empNo) }}</td><td>{{ r.regJobGroup }}</td><td>{{ r.csvJobGroup }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h6>Duplicate payroll IDs ({{ duplicateRows.length }})</h6>
          <div class="table-responsive hr-mb-3" style="max-height: 200px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Occurrences</th><th>Total pay</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in duplicateRows" :key="'dup-' + i">
                  <td>{{ displayId(r.empNo || r.staffId) }}</td>
                  <td>{{ r.count }}</td>
                  <td>{{ formatMoney(r.totalPay) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h6>Inactive on register but paid on payroll ({{ inactivePaidRows.length }})</h6>
          <div class="table-responsive" style="max-height: 200px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Name</th><th>Status</th><th>Pay</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in inactivePaidRows" :key="'inact-' + i">
                  <td>{{ displayId(r.empNo) }}</td>
                  <td>{{ r.regName || r.name }}</td>
                  <td>{{ r.status }}</td>
                  <td>{{ formatMoney(r.pay) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'audit'" class="table-card">
          <button type="button" class="hr-btn hr-btn-outline-secondary hr-btn-sm hr-mb-2" :disabled="auditLogLoading" @click="loadAuditLog">
            {{ auditLogLoading ? "Loading…" : "Refresh audit log" }}
          </button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr><th>When</th><th>Action</th><th>Actor</th><th>Details</th></tr>
              </thead>
              <tbody>
                <tr v-if="auditLogLoading"><td colspan="4" class="text-muted">Loading…</td></tr>
                <tr v-else-if="!auditLogs.length"><td colspan="4" class="text-muted">No audit entries for this period.</td></tr>
                <tr v-for="log in auditLogs" :key="'aud-' + log.id">
                  <td>{{ log.created_at }}</td>
                  <td>{{ log.action }}</td>
                  <td>{{ log.actor_username || "—" }}</td>
                  <td class="hr-small">{{ formatAuditDetails(log.details_json) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'spending'" class="table-card">
          <p class="text-muted hr-small"><strong>SPENDING ENTITY SUMMARY</strong> — grouped by Employee Register department</p>
          <p v-if="!spendingSummaryRows.length && analysisDone" class="text-warning hr-small">
            No matched employees to group by register department.
          </p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('spending')">Download costing summary CSV</button>
          <div class="table-responsive" style="max-height: 520px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Department (spending entity)</th>
                  <th>Staff earning</th>
                  <th>Basic pay</th>
                  <th>Rental house all.</th>
                  <th>Commuter all.</th>
                  <th>Other allowances</th>
                  <th>Gross salary</th>
                  <th>NSSF</th>
                  <th>Housing levy</th>
                  <th>Pension (employer)</th>
                  <th>NITA</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in spendingSummaryRows" :key="'sp-' + row.seq + '-' + row.key">
                  <td>{{ row.seq }}</td>
                  <td>{{ row.entity }}</td>
                  <td>{{ row.staffCount }}</td>
                  <td>{{ formatMoney(row.basic) }}</td>
                  <td>{{ formatMoney(row.rental) }}</td>
                  <td>{{ formatMoney(row.commuter) }}</td>
                  <td>{{ formatMoney(row.otherAllow) }}</td>
                  <td>{{ formatMoney(row.gross) }}</td>
                  <td>{{ formatMoney(row.nssf) }}</td>
                  <td>{{ formatMoney(row.housing) }}</td>
                  <td>{{ formatMoney(row.pensionEmployer) }}</td>
                  <td>{{ formatMoney(row.nita) }}</td>
                  <td>{{ formatMoney(row.total) }}</td>
                </tr>
                <tr v-if="spendingSummaryRows.length" class="table-secondary fw-bold">
                  <td colspan="2">County wage bill (total)</td>
                  <td>{{ spendingTotals.staffCount }}</td>
                  <td>{{ formatMoney(spendingTotals.basic) }}</td>
                  <td>{{ formatMoney(spendingTotals.rental) }}</td>
                  <td>{{ formatMoney(spendingTotals.commuter) }}</td>
                  <td>{{ formatMoney(spendingTotals.otherAllow) }}</td>
                  <td>{{ formatMoney(spendingTotals.gross) }}</td>
                  <td>{{ formatMoney(spendingTotals.nssf) }}</td>
                  <td>{{ formatMoney(spendingTotals.housing) }}</td>
                  <td>{{ formatMoney(spendingTotals.pensionEmployer) }}</td>
                  <td>{{ formatMoney(spendingTotals.nita) }}</td>
                  <td>{{ formatMoney(spendingTotals.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'wagebill'" class="table-card">
          <p class="text-muted hr-small">Totals by department / work station column from the ledger (or mapped department field).</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('wagebill')">Download CSV</button>
          <div class="table-responsive">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Department (CSV)</th>
                  <th>Headcount</th>
                  <th>Total pay</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in wageBillRows" :key="row.dept">
                  <td>{{ row.dept }}</td>
                  <td>{{ row.count }}</td>
                  <td>{{ formatMoney(row.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'spendingDetail'" class="table-card">
          <p class="text-muted hr-small">Detailed report per register department (spending entity).</p>
          <div class="d-flex flex-wrap gap-2 hr-mb-2">
            <select v-model="selectedSpendingEntityDetail" class="filter-select" style="max-width: 360px;">
              <option value="">All spending entities</option>
              <option v-for="e in spendingEntityOptions" :key="'seo-' + e" :value="e">{{ e }}</option>
            </select>
          </div>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('spendingDetail')">Download CSV</button>
          <div class="table-responsive" style="max-height: 520px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>PAYROLL No.</th>
                  <th>ID. No.</th>
                  <th>NAME</th>
                  <th>COUNTY SPENDING ENTITY</th>
                  <th>Basic Pay</th>
                  <th>Rental House All.</th>
                  <th>Commuter All.</th>
                  <th>Other Allowances</th>
                  <th>GrossPay</th>
                  <th>Projected Annual Increment (4% of Basic Pay)</th>
                  <th>NSSF</th>
                  <th>H/Levy</th>
                  <th>Pension (Employer)</th>
                  <th>NITA</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in filteredSpendingDetailRows" :key="'sdr-' + i">
                  <td>{{ i + 1 }}</td>
                  <td>{{ r.payrollNo }}</td>
                  <td>{{ r.nationalId }}</td>
                  <td>{{ r.name }}</td>
                  <td>{{ r.entity }}</td>
                  <td>{{ formatMoney(r.basic) }}</td>
                  <td>{{ formatMoney(r.rental) }}</td>
                  <td>{{ formatMoney(r.commuter) }}</td>
                  <td>{{ formatMoney(r.otherAllow) }}</td>
                  <td>{{ formatMoney(r.gross) }}</td>
                  <td>{{ formatMoney(r.projectedIncrement) }}</td>
                  <td>{{ formatMoney(r.nssf) }}</td>
                  <td>{{ formatMoney(r.housing) }}</td>
                  <td>{{ formatMoney(r.pensionEmployer) }}</td>
                  <td>{{ formatMoney(r.nita) }}</td>
                </tr>
                <tr v-if="filteredSpendingDetailRows.length" class="table-secondary fw-bold">
                  <td colspan="5">Totals</td>
                  <td>{{ formatMoney(spendingDetailTotals.basic) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.rental) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.commuter) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.otherAllow) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.gross) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.projectedIncrement) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.nssf) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.housing) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.pensionEmployer) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.nita) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'registerOnly'" class="table-card">
          <p class="text-muted hr-small"><strong>Missing from payroll upload:</strong> employees in HRMIS (register) not found on the file (matched by Payroll / Emp No or National ID / IDNum).</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('registerOnly')">Download CSV</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Emp No</th>
                  <th>National ID</th>
                  <th>Name</th>
                  <th>Department (register)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(e, i) in registerOnlyRows" :key="'ro-' + i">
                  <td>{{ e.empNo }}</td>
                  <td>{{ e.nationalId }}</td>
                  <td>{{ fullName(e) }}</td>
                  <td>{{ e.departmentName || e.departmentID }}</td>
                  <td>{{ e.status }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'csvOnly'" class="table-card">
          <p class="text-muted hr-small">CSV rows whose staff ID did not match any employee (Emp No or National ID).</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('csvOnly')">Download CSV</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Staff ID (CSV)</th>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Pay</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in csvOnlyRows" :key="'co-' + i">
                  <td>{{ r.staffId }}</td>
                  <td>{{ r.name }}</td>
                  <td>{{ r.dept }}</td>
                  <td>{{ formatMoney(r.pay) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'matched'" class="table-card">
          <p class="text-muted hr-small">Rows where CSV staff ID matched the register. “Dept mismatch” flags when CSV department text ≠ register department name.</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('matched')">Download CSV</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Emp No</th>
                  <th>Name (register)</th>
                  <th>Dept (register)</th>
                  <th>Dept (CSV)</th>
                  <th>Dept mismatch</th>
                  <th>Pay (CSV)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in matchedDetailRows" :key="'m-' + i">
                  <td>{{ r.empNo }}</td>
                  <td>{{ r.regName }}</td>
                  <td>{{ r.regDept }}</td>
                  <td>{{ r.csvDept }}</td>
                  <td>{{ r.deptMismatch ? 'Yes' : '' }}</td>
                  <td>{{ formatMoney(r.pay) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  normId,
  pickColumn,
  parseMoney,
  detectCodeColumns,
  runRegisterPayrollAnalysis,
  reconcilePension,
  reconcileGratuity,
  buildExecutiveSummary,
  maskSensitive,
  buildRegisterIndex
} from "@/utils/payrollReconciliationEngine";

function normKeyHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export default {
  name: "PayrollCsvReconciliation",
  components: { HrPageLayout },
  data() {
    return {
      breadcrumbItems: [
        { label: "Reports & Analytics", route: "/reports-hub" },
        { label: "Payroll ledger reconciliation" }
      ],
      csvRows: [],
      headers: [],
      registerScope: "active",
      loading: false,
      errorMsg: "",
      analysisDone: false,
      activeTab: "saved",
      mapId: "",
      mapDept: "",
      mapPay: "",
      mapName: "",
      selectedSpendingEntityDetail: "",
      periodYear: new Date().getFullYear(),
      periodMonth: new Date().getMonth() + 1,
      nitaPerEmployee: 50,
      savingNita: false,
      periodFinalized: false,
      periodId: null,
      canManage: true,
      canFinalize: false,
      canReopen: false,
      maskSensitiveData: false,
      preValidation: null,
      uploadStatus: {},
      stagedFiles: {},
      uploadingAll: false,
      uploadProgress: 0,
      uploadErrors: [],
      pensionRows: { '16': [], '32': [], '33': [], '101': [] },
      pensionHeaders: { '16': [], '32': [], '33': [], '101': [] },
      gratuityRows: [],
      gratuityHeaders: [],
      detectedEarnings: [],
      detectedDeductions: [],
      pensionRecon: null,
      gratuityRecon: null,
      executiveSummary: null,
      financialSummary: null,
      deptVariances: [],
      designationVariances: [],
      jobGroupVariances: [],
      duplicateRows: [],
      inactivePaidRows: [],
      registerIndex: null,
      employees: [],
      savedReports: [],
      savedReportsLoading: false,
      savingReport: false,
      saveReportMessage: "",
      saveReportError: false,
      summary: {
        csvRowCount: 0,
        registerCount: 0,
        matchedCount: 0,
        csvOnlyCount: 0,
        registerOnlyCount: 0,
        totalCsvPay: 0
      },
      wageBillRows: [],
      registerOnlyRows: [],
      csvOnlyRows: [],
      matchedDetailRows: [],
      spendingSummaryRows: [],
      spendingDetailRows: [],
      tabs: [
        { id: "saved", label: "Saved reports", requiresAnalysis: false },
        { id: "executive", label: "Spending Entities Summary Report", requiresAnalysis: true },
        { id: "summary", label: "Summary", requiresAnalysis: true },
        { id: "spending", label: "Spending entities", requiresAnalysis: true },
        { id: "spendingDetail", label: "Dept detail", requiresAnalysis: true },
        { id: "pension", label: "Pension (902)", requiresAnalysis: true },
        { id: "gratuity", label: "Gratuity", requiresAnalysis: true },
        { id: "nita", label: "NITA", requiresAnalysis: true },
        { id: "variances", label: "Variances", requiresAnalysis: true },
        { id: "wagebill", label: "CSV wage bill", requiresAnalysis: true },
        { id: "registerOnly", label: "Missing from payroll", requiresAnalysis: true },
        { id: "csvOnly", label: "Extra on payroll", requiresAnalysis: true },
        { id: "matched", label: "Matched detail", requiresAnalysis: true },
        { id: "audit", label: "Audit log", requiresAnalysis: false }
      ],
      auditLogs: [],
      auditLogLoading: false,
      hydratingFiles: false
    };
  },
  computed: {
    monthOptions() {
      return [
        { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
        { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
        { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
        { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
      ];
    },
    yearOptions() {
      const y = new Date().getFullYear();
      return [y - 1, y, y + 1];
    },
    periodLabel() {
      const m = this.monthOptions.find((x) => x.value === this.periodMonth);
      return `${m ? m.label : this.periodMonth} ${this.periodYear}`;
    },
    pensionFilesReady() {
      if (!this.preValidation || !this.preValidation.checks) return false;
      return this.preValidation.checks.pension_16 && this.preValidation.checks.pension_32 &&
        this.preValidation.checks.pension_33 && this.preValidation.checks.pension_101;
    },
    canRunAnalysis() {
      if (this.periodFinalized || this.hydratingFiles || !this.canManage) return false;
      const pensionLocal = [16, 32, 33, 101].every((c) => (this.pensionRows[c] || []).length > 0);
      const gratuityLocal = (this.gratuityRows || []).length > 0;
      return this.csvRows.length > 0 && pensionLocal && gratuityLocal && Number(this.nitaPerEmployee) > 0;
    },
    spendingTotals() {
      const z = {
        staffCount: 0,
        basic: 0,
        rental: 0,
        commuter: 0,
        otherAllow: 0,
        gross: 0,
        nssf: 0,
        housing: 0,
        pensionEmployer: 0,
        nita: 0,
        total: 0
      };
      for (const row of this.spendingSummaryRows) {
        z.staffCount += Number(row.staffCount) || 0;
        z.basic += Number(row.basic) || 0;
        z.rental += Number(row.rental) || 0;
        z.commuter += Number(row.commuter) || 0;
        z.otherAllow += Number(row.otherAllow) || 0;
        z.gross += Number(row.gross) || 0;
        z.nssf += Number(row.nssf) || 0;
        z.housing += Number(row.housing) || 0;
        z.pensionEmployer += Number(row.pensionEmployer) || 0;
        z.nita += Number(row.nita) || 0;
        z.total += Number(row.total) || 0;
      }
      return z;
    },
    spendingEntityOptions() {
      const set = new Set();
      (this.spendingDetailRows || []).forEach((r) => {
        const k = String(r.entity || "").trim();
        if (k) set.add(k);
      });
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    },
    filteredSpendingDetailRows() {
      const selected = String(this.selectedSpendingEntityDetail || "").trim();
      if (!selected) return this.spendingDetailRows || [];
      return (this.spendingDetailRows || []).filter((r) => String(r.entity || "").trim() === selected);
    },
    spendingDetailTotals() {
      const z = {
        basic: 0,
        rental: 0,
        commuter: 0,
        otherAllow: 0,
        gross: 0,
        projectedIncrement: 0,
        nssf: 0,
        housing: 0,
        pensionEmployer: 0,
        nita: 0
      };
      for (const r of this.filteredSpendingDetailRows || []) {
        z.basic += Number(r.basic) || 0;
        z.rental += Number(r.rental) || 0;
        z.commuter += Number(r.commuter) || 0;
        z.otherAllow += Number(r.otherAllow) || 0;
        z.gross += Number(r.gross) || 0;
        z.projectedIncrement += Number(r.projectedIncrement) || 0;
        z.nssf += Number(r.nssf) || 0;
        z.housing += Number(r.housing) || 0;
        z.pensionEmployer += Number(r.pensionEmployer) || 0;
        z.nita += Number(r.nita) || 0;
      }
      return z;
    }
  },
  mounted() {
    this.loadCapabilities();
    this.loadPeriodStatus();
    this.loadSavedReports();
  },
  methods: {
    displayId(v) {
      return maskSensitive(v, this.maskSensitiveData);
    },
    async loadCapabilities() {
      try {
        const data = new FormData();
        data.append("function", "getPayrollReconCapabilities");
        const res = await execute(data, authUrl);
        if (res.data && res.data.success && res.data.data) {
          this.canManage = !!res.data.data.canManage;
          this.canFinalize = !!res.data.data.canFinalize;
          this.canReopen = !!res.data.data.canReopen;
          this.maskSensitiveData = !!res.data.data.maskSensitive;
        } else if (res.data && !res.data.success) {
          // Server denied — not a network error
          this.canManage = false;
          const msg = res.data.message || "Permission check failed.";
          if (!msg.toLowerCase().includes("permission") && !msg.toLowerCase().includes("unauthorized")) {
            this.errorMsg = "Could not load upload permissions: " + msg;
          }
        }
        // If response has no data at all, keep canManage = true (optimistic — server will enforce)
      } catch (e) {
        // Network / parse error — keep canManage = true so uploads can still be attempted
        console.warn("loadCapabilities failed:", e);
      }
    },
    async loadPeriodStatus() {
      try {
        const data = new FormData();
        data.append("function", "getPayrollReconPeriodStatus");
        data.append("periodYear", String(this.periodYear));
        data.append("periodMonth", String(this.periodMonth));
        const res = await execute(data, authUrl);
        if (res.data && res.data.success && res.data.data) {
          const d = res.data.data;
          this.periodFinalized = !!d.finalized;
          this.periodId = d.period ? d.period.id : null;
          this.preValidation = d.validation || null;
          if (d.nita && d.nita.amount_per_employee != null) {
            this.nitaPerEmployee = Number(d.nita.amount_per_employee);
          }
          const uploads = d.uploads || [];
          const st = {};
          uploads.forEach((u) => {
            if (u.file_type && !st[u.file_type]) st[u.file_type] = u.original_name;
          });
          this.uploadStatus = st;
          await this.hydrateFilesFromServer();
        }
      } catch (_) {
        this.preValidation = null;
      }
    },
    async onPeriodChange() {
      this.analysisDone = false;
      this.csvRows = [];
      this.headers = [];
      this.pensionRows = { '16': [], '32': [], '33': [], '101': [] };
      this.pensionHeaders = { '16': [], '32': [], '33': [], '101': [] };
      this.gratuityRows = [];
      this.gratuityHeaders = [];
      this.stagedFiles = {};
      this.uploadErrors = [];
      this.uploadProgress = 0;
      await this.loadPeriodStatus();
    },
    formatReportPeriod(r) {
      const y = Number(r.period_year);
      const m = Number(r.period_month);
      if (!y || !m) return "—";
      const mo = this.monthOptions.find((x) => x.value === m);
      return `${mo ? mo.label : m} ${y}`;
    },
    formatAuditDetails(raw) {
      if (!raw) return "—";
      try {
        const o = typeof raw === "string" ? JSON.parse(raw) : raw;
        return JSON.stringify(o);
      } catch (_) {
        return String(raw);
      }
    },
    async loadAuditLog() {
      this.auditLogLoading = true;
      try {
        const data = new FormData();
        data.append("function", "listPayrollReconAuditLog");
        if (this.periodId) data.append("periodId", String(this.periodId));
        data.append("limit", "100");
        const res = await execute(data, authUrl);
        this.auditLogs = res.data?.data?.logs || [];
      } catch (_) {
        this.auditLogs = [];
      } finally {
        this.auditLogLoading = false;
      }
    },
    parseRowsFromBuffer(buffer, fileName) {
      const name = (fileName || "").toLowerCase();
      if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
        const data = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
        const wb = XLSX.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
        const rows = json.map((row) => {
          const o = {};
          for (const k of Object.keys(row)) o[String(k).trim()] = row[k];
          return o;
        }).filter((r) => Object.keys(r).some((k) => String(r[k] || "").trim() !== ""));
        return { rows, headers: rows[0] ? Object.keys(rows[0]) : [] };
      }
      const text = new TextDecoder("utf-8").decode(buffer);
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: "greedy" });
      const rows = (parsed.data || []).filter((r) => Object.keys(r).some((k) => String(r[k] || "").trim() !== ""));
      return { rows, headers: parsed.meta.fields || (rows[0] ? Object.keys(rows[0]) : []) };
    },
    async fetchUploadContent(fileType) {
      const data = new FormData();
      data.append("function", "getPayrollReconUploadContent");
      data.append("periodYear", String(this.periodYear));
      data.append("periodMonth", String(this.periodMonth));
      data.append("fileType", fileType);
      const res = await execute(data, authUrl);
      if (!res.data || !res.data.success || !res.data.data) {
        throw new Error(res.data?.message || `Could not load ${fileType}`);
      }
      return res.data.data;
    },
    async hydrateFilesFromServer() {
      const types = ["payroll", "pension_16", "pension_32", "pension_33", "pension_101", "gratuity"];
      const need = types.filter((t) => {
        if (!this.uploadStatus[t]) return false;
        if (t === "payroll" && this.csvRows && this.csvRows.length > 0) return false;
        if (t === "gratuity" && this.gratuityRows && this.gratuityRows.length > 0) return false;
        if (t.startsWith("pension_")) {
          const code = t.replace("pension_", "");
          if (this.pensionRows[code] && this.pensionRows[code].length > 0) return false;
        }
        return true;
      });
      if (!need.length) return;
      this.hydratingFiles = true;
      try {
        for (const fileType of need) {
          const payload = await this.fetchUploadContent(fileType);
          const bin = atob(payload.contentBase64 || "");
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          const { rows, headers } = this.parseRowsFromBuffer(bytes, payload.originalName || fileType);
          if (fileType === "payroll") {
            this.csvRows = rows;
            this.headers = headers;
            this.autoMapColumns();
            const codes = detectCodeColumns(headers);
            this.detectedEarnings = codes.earnings;
            this.detectedDeductions = codes.deductions;
          } else if (fileType.startsWith("pension_")) {
            const code = fileType.replace("pension_", "");
            if (["16", "32", "33", "101"].includes(code)) {
              this.pensionRows[code] = rows;
              this.pensionHeaders[code] = headers;
            }
          } else if (fileType === "gratuity") {
            this.gratuityRows = rows;
            this.gratuityHeaders = headers;
          }
        }
      } catch (e) {
        this.errorMsg = e.message || String(e);
      } finally {
        this.hydratingFiles = false;
      }
    },
    async saveNitaConfig() {
      if (!this.nitaPerEmployee || this.nitaPerEmployee <= 0) {
        this.errorMsg = "Enter a valid NITA amount per employee.";
        return;
      }
      this.savingNita = true;
      try {
        const data = new FormData();
        data.append("function", "savePayrollReconNita");
        data.append("periodYear", String(this.periodYear));
        data.append("periodMonth", String(this.periodMonth));
        data.append("amountPerEmployee", String(this.nitaPerEmployee));
        const res = await execute(data, authUrl);
        if (!res.data || !res.data.success) throw new Error(res.data?.message || "Failed to save NITA");
        await this.loadPeriodStatus();
        this.saveReportMessage = "NITA amount saved.";
        this.saveReportError = false;
      } catch (e) {
        this.errorMsg = e.message || String(e);
      } finally {
        this.savingNita = false;
      }
    },
    async uploadFileToServer(file, fileType, rowCount, headers) {
      const data = new FormData();
      data.append("function", "uploadPayrollReconFile");
      data.append("periodYear", String(this.periodYear));
      data.append("periodMonth", String(this.periodMonth));
      data.append("fileType", fileType);
      data.append("file", file);
      data.append("rowCount", String(rowCount || 0));
      data.append("headersJson", JSON.stringify(headers || []));
      const res = await execute(data, authUrl);
      if (!res.data || !res.data.success) {
        throw new Error(res.data?.message || "Upload failed");
      }
      return res.data.data;
    },
    parseFile(file) {
      return new Promise((resolve, reject) => {
        const name = (file.name || "").toLowerCase();
        if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const data = new Uint8Array(e.target.result);
              const wb = XLSX.read(data, { type: "array" });
              const sheet = wb.Sheets[wb.SheetNames[0]];
              const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
              const rows = json.map((row) => {
                const o = {};
                for (const k of Object.keys(row)) o[String(k).trim()] = row[k];
                return o;
              }).filter((r) => Object.keys(r).some((k) => String(r[k] || "").trim() !== ""));
              resolve({ rows, headers: rows[0] ? Object.keys(rows[0]) : [] });
            } catch (err) {
              reject(err);
            }
          };
          reader.onerror = () => reject(new Error("Could not read file"));
          reader.readAsArrayBuffer(file);
          return;
        }
        Papa.parse(file, {
          header: true,
          skipEmptyLines: "greedy",
          complete: (res) => {
            const rows = (res.data || []).filter((r) => Object.keys(r).some((k) => String(r[k] || "").trim() !== ""));
            resolve({ rows, headers: res.meta.fields || (rows[0] ? Object.keys(rows[0]) : []) });
          },
          error: (err) => reject(err)
        });
      });
    },
    stageFile(ev, type) {
      const f = ev.target.files && ev.target.files[0];
      if (!f) {
        const newStaged = { ...this.stagedFiles };
        delete newStaged[type];
        this.stagedFiles = newStaged;
        return;
      }
      this.stagedFiles = { ...this.stagedFiles, [type]: f };
      this.errorMsg = "";
      this.analysisDone = false;
    },
    async uploadAllStagedFiles() {
      if (this.periodFinalized) {
        this.errorMsg = "This month is finalized. Uploads are blocked. A Super Admin must reopen the month first.";
        return;
      }
      const types = Object.keys(this.stagedFiles);
      if (types.length === 0) return;
      
      this.uploadingAll = true;
      this.uploadErrors = [];
      this.uploadProgress = 0;
      this.errorMsg = "";
      
      let completedCount = 0;
      
      for (const type of types) {
        const f = this.stagedFiles[type];
        try {
          const { rows, headers } = await this.parseFile(f);
          
          if (type === "payroll") {
            this.csvRows = rows;
            this.headers = headers;
            this.autoMapColumns();
            const codes = detectCodeColumns(headers);
            this.detectedEarnings = codes.earnings;
            this.detectedDeductions = codes.deductions;
          } else if (type.startsWith("pension_")) {
            const index = type.replace("pension_", "");
            this.pensionRows[index] = rows;
            this.pensionHeaders[index] = headers;
          } else if (type === "gratuity") {
            this.gratuityRows = rows;
            this.gratuityHeaders = headers;
          }
          
          await this.uploadFileToServer(f, type, rows.length, headers);
          
          this.uploadStatus = { ...this.uploadStatus, [type]: f.name };
          const newStaged = { ...this.stagedFiles };
          delete newStaged[type];
          this.stagedFiles = newStaged;
        } catch (e) {
          const typeLabel = type === "payroll" ? "Payroll ledger" : 
                            type === "gratuity" ? "Gratuity report" : 
                            `Pension file ${type.replace("pension_", "")}`;
          this.uploadErrors.push(`${typeLabel} (${f.name}): ${e.message || String(e)}`);
        }
        
        completedCount++;
        this.uploadProgress = Math.round((completedCount / types.length) * 100);
      }
      
      await this.loadPeriodStatus();
      this.uploadingAll = false;
      setTimeout(() => {
        if (this.uploadProgress === 100 && this.uploadErrors.length === 0) {
          this.uploadProgress = 0;
        }
      }, 3000);
    },
    async finalizeMonth() {
      if (!window.confirm(`Finalize ${this.periodLabel}? This locks uploads and re-analysis.`)) return;
      const data = new FormData();
      data.append("function", "finalizePayrollReconMonth");
      data.append("periodYear", String(this.periodYear));
      data.append("periodMonth", String(this.periodMonth));
      const res = await execute(data, authUrl);
      if (res.data && res.data.success) {
        await this.loadPeriodStatus();
        this.saveReportMessage = res.data.message || "Month finalized.";
      } else {
        this.errorMsg = res.data?.message || "Could not finalize.";
      }
    },
    async reopenMonth() {
      const reason = window.prompt("Reason for reopening this month (required):");
      if (!reason || !reason.trim()) return;
      const data = new FormData();
      data.append("function", "reopenPayrollReconMonth");
      data.append("periodYear", String(this.periodYear));
      data.append("periodMonth", String(this.periodMonth));
      data.append("reason", reason.trim());
      const res = await execute(data, authUrl);
      if (res.data && res.data.success) {
        await this.loadPeriodStatus();
        this.saveReportMessage = "Month reopened.";
      } else {
        this.errorMsg = res.data?.message || "Could not reopen.";
      }
    },
    async saveTemplateCatalog(codes) {
      if (!codes || !codes.length) return;
      try {
        const data = new FormData();
        data.append("function", "savePayrollReconTemplateCatalog");
        data.append("codesJson", JSON.stringify(codes));
        await execute(data, authUrl);
      } catch (_) { /* non-blocking */ }
    },
    fullName(e) {
      const m = e.middleName ? ` ${e.middleName} ` : " ";
      return `${e.firstName || ""}${m}${e.lastName || ""}`.trim();
    },
    formatMoney(n) {
      if (n == null || Number.isNaN(n)) return "—";
      return new Intl.NumberFormat("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    },
    onTabClick(id) {
      const t = this.tabs.find((x) => x.id === id);
      if (t && t.requiresAnalysis && !this.analysisDone) return;
      this.activeTab = id;
      if (id === "saved") this.loadSavedReports();
      if (id === "audit") this.loadAuditLog();
    },
    async loadSavedReports() {
      this.savedReportsLoading = true;
      try {
        const data = new FormData();
        data.append("function", "listPayrollCsvReconciliationReports");
        data.append("limit", "50");
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          this.savedReports = res.data.data?.reports || [];
        } else {
          this.savedReports = [];
        }
      } catch (_) {
        this.savedReports = [];
      } finally {
        this.savedReportsLoading = false;
      }
    },
    openSaveReport() {
      this.saveReportMessage = "";
      const def = `Payroll reconciliation ${new Date().toISOString().slice(0, 10)}`;
      const title = window.prompt("Title for this saved report", def);
      if (title === null) return;
      this.saveReportToServer(title.trim() || def);
    },
    buildReportPayload() {
      return {
        version: 2,
        periodYear: this.periodYear,
        periodMonth: this.periodMonth,
        summary: { ...this.summary },
        financialSummary: this.financialSummary,
        executiveSummary: this.executiveSummary,
        pensionRecon: this.pensionRecon,
        gratuityRecon: this.gratuityRecon,
        spendingSummaryRows: this.spendingSummaryRows,
        wageBillRows: this.wageBillRows,
        registerOnlyRows: this.registerOnlyRows,
        csvOnlyRows: this.csvOnlyRows,
        matchedDetailRows: this.matchedDetailRows,
        spendingDetailRows: this.spendingDetailRows,
        deptVariances: this.deptVariances,
        designationVariances: this.designationVariances,
        jobGroupVariances: this.jobGroupVariances,
        duplicateRows: this.duplicateRows,
        inactivePaidRows: this.inactivePaidRows,
        columnMaps: {
          mapId: this.mapId,
          mapDept: this.mapDept,
          mapPay: this.mapPay,
          mapName: this.mapName
        },
        headers: [...this.headers],
        nitaPerEmployee: this.nitaPerEmployee
      };
    },
    async saveReportToServer(title) {
      this.savingReport = true;
      this.saveReportMessage = "";
      this.saveReportError = false;
      try {
        const payload = JSON.stringify(this.buildReportPayload());
        const data = new FormData();
        data.append("function", "savePayrollCsvReconciliationReport");
        data.append("title", title);
        data.append("registerScope", this.registerScope);
        data.append("periodYear", String(this.periodYear));
        data.append("periodMonth", String(this.periodMonth));
        data.append("employeeCount", String(this.summary.matchedCount || 0));
        data.append("grossPayroll", String(this.financialSummary?.grossPay || this.summary.grossPay || 0));
        data.append("pensionTotal", String(this.pensionRecon?.totals?.pensionFiles902 || 0));
        data.append("gratuityTotal", String(this.gratuityRecon?.uploadedTotal || 0));
        data.append("nitaTotal", String(this.summary.nitaExpected || 0));
        data.append("varianceTotal", String(this.executiveSummary?.totalVariance || 0));
        data.append("payload", payload);
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          this.saveReportMessage = res.data.message || "Report saved.";
          this.saveReportError = false;
          await this.loadSavedReports();
          this.activeTab = "saved";
        } else {
          this.saveReportMessage = res.data?.message || "Could not save report.";
          this.saveReportError = true;
        }
      } catch (e) {
        this.saveReportMessage = e.message || "Could not save report.";
        this.saveReportError = true;
      } finally {
        this.savingReport = false;
      }
    },
    async loadSavedReport(id) {
      this.errorMsg = "";
      try {
        const data = new FormData();
        data.append("function", "getPayrollCsvReconciliationReport");
        data.append("id", String(id));
        const res = await execute(data, authUrl);
        if (!res.data || !res.data.success) {
          throw new Error(res.data?.message || "Failed to load report");
        }
        const rep = res.data.data?.report;
        const p = rep?.payload;
        if (!p || typeof p !== "object") {
          throw new Error("Invalid saved data");
        }
        this.applyReportPayload(p);
        if (rep.register_scope) this.registerScope = rep.register_scope;
        this.analysisDone = true;
        this.activeTab = "summary";
        this.saveReportMessage = `Loaded: ${rep.title || "Report #" + id}`;
        this.saveReportError = false;
      } catch (e) {
        this.errorMsg = e.message || String(e);
      }
    },
    applyReportPayload(p) {
      this.summary = { ...(p.summary || {}) };
      this.financialSummary = p.financialSummary || null;
      this.executiveSummary = p.executiveSummary || null;
      this.pensionRecon = p.pensionRecon || null;
      this.gratuityRecon = p.gratuityRecon || null;
      this.deptVariances = Array.isArray(p.deptVariances) ? p.deptVariances : [];
      this.designationVariances = Array.isArray(p.designationVariances) ? p.designationVariances : [];
      this.jobGroupVariances = Array.isArray(p.jobGroupVariances) ? p.jobGroupVariances : [];
      this.duplicateRows = Array.isArray(p.duplicateRows) ? p.duplicateRows : [];
      this.inactivePaidRows = Array.isArray(p.inactivePaidRows) ? p.inactivePaidRows : [];
      const srows = Array.isArray(p.spendingSummaryRows) ? p.spendingSummaryRows : [];
      this.spendingSummaryRows = srows.map((r, i) => ({
        ...r,
        key: r.key != null ? r.key : `legacy-${r.seq ?? i}-${r.entity ?? ""}`
      }));
      this.wageBillRows = Array.isArray(p.wageBillRows) ? p.wageBillRows : [];
      this.registerOnlyRows = Array.isArray(p.registerOnlyRows) ? p.registerOnlyRows : [];
      this.csvOnlyRows = Array.isArray(p.csvOnlyRows) ? p.csvOnlyRows : [];
      this.matchedDetailRows = Array.isArray(p.matchedDetailRows) ? p.matchedDetailRows : [];
      this.spendingDetailRows = Array.isArray(p.spendingDetailRows) ? p.spendingDetailRows : [];
      const cm = p.columnMaps || {};
      this.mapId = cm.mapId ?? "";
      this.mapDept = cm.mapDept ?? "";
      this.mapPay = cm.mapPay ?? "";
      this.mapName = cm.mapName ?? "";
      if (p.periodYear) this.periodYear = p.periodYear;
      if (p.periodMonth) this.periodMonth = p.periodMonth;
      if (p.nitaPerEmployee != null) this.nitaPerEmployee = p.nitaPerEmployee;
      this.headers = Array.isArray(p.headers) ? [...p.headers] : [];
      this.csvRows = [];
    },
    async deleteSavedReport(id) {
      if (!window.confirm("Delete this saved report?")) return;
      try {
        const data = new FormData();
        data.append("function", "deletePayrollCsvReconciliationReport");
        data.append("id", String(id));
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          await this.loadSavedReports();
        } else {
          this.errorMsg = res.data?.message || "Delete failed";
        }
      } catch (e) {
        this.errorMsg = e.message || String(e);
      }
    },
    autoMapColumns() {
      const h = this.headers;
      this.mapId =
        pickColumn(h, [
          "payrollnum",
          "payroll num",
          "idnum",
          "id num",
          "national id",
          "nationalid",
          "pf no",
          "p/f no",
          "pfno",
          "emp no",
          "employee no",
          "staff no",
          "personal file",
          "service number",
          "id no",
          "staff id",
          "employee id",
          "empno",
          "pf number",
          "taxpin",
          "tax pin"
        ]) || "";
      this.mapDept =
        pickColumn(h, [
          "wstationname",
          "work station",
          "station",
          "department",
          "dept",
          "directorate",
          "unit",
          "wcountyname",
          "county"
        ]) || "";
      this.mapPay =
        pickColumn(h, [
          "grosspay",
          "gross pay",
          "gross salary",
          "gross",
          "net pay",
          "net salary",
          "netpay",
          "net",
          "basic pay",
          "basic salary",
          "basic",
          "total pay",
          "amount",
          "pay"
        ]) || "";
      this.mapName =
        pickColumn(h, ["names", "employee name", "staff name", "full name", "name", "surname"]) || "";
    },
    resolvedMaps() {
      const h = this.headers;
      const idCol =
        this.mapId ||
        pickColumn(h, ["payrollnum", "idnum", "pf", "emp", "staff", "employee", "national"]);
      const deptCol =
        this.mapDept ||
        pickColumn(h, ["department", "dept", "wstation", "station", "work station", "wstationname"]);
      let payCol = this.mapPay;
      if (!payCol) {
        payCol =
          pickColumn(h, ["grosspay", "gross"]) ||
          pickColumn(h, ["net"]) ||
          pickColumn(h, ["basic"]) ||
          pickColumn(h, ["amount", "pay", "salary"]);
      }
      const nameCol = this.mapName || pickColumn(h, ["names", "name"]);
      return { idCol, deptCol, payCol, nameCol };
    },

    enrichSpendingSummaryFromLedger(baseRows) {
      const h = this.headers;
      const basicCol = pickColumn(h, ["801 basic", "basic salary", "basic pay"]);
      const rentalCol = pickColumn(h, ["802 rental", "rental house all"]);
      const commuterCol = pickColumn(h, ["813 commuter", "commuter all"]);
      const nssfCol = pickColumn(h, ["933 nssf", "nssf auto", "nssf"]);
      const housingCol = pickColumn(h, ["944 housing", "housing levy"]);
      const pensionEmpCol = pickColumn(h, ["pension (employer)", "employer pension"]);
      const idCol = this.mapId || pickColumn(h, ["payrollnum", "idnum"]);
      const hasPensionEmpCol = !!pensionEmpCol;
      const byEntity = new Map(
        (baseRows || []).map((r) => [
          String(r.entity).toLowerCase(),
          {
            ...r,
            basic: 0,
            rental: 0,
            commuter: 0,
            nssf: 0,
            housing: 0,
            pensionEmployer: hasPensionEmpCol ? 0 : (Number(r.pension902) || 0),
            otherAllow: 0,
            total: 0
          }
        ])
      );
      for (const row of this.csvRows) {
        const rawId = idCol ? row[idCol] : null;
        const emp = rawId && this.registerIndex ? this.registerIndex.get(normId(rawId)) : null;
        if (!emp) continue;
        const entity = String(emp.departmentName || "(No department)").trim();
        const key = entity.toLowerCase();
        const agg = byEntity.get(key);
        if (!agg) continue;
        if (basicCol) agg.basic += parseMoney(row[basicCol]);
        if (rentalCol) agg.rental += parseMoney(row[rentalCol]);
        if (commuterCol) agg.commuter += parseMoney(row[commuterCol]);
        if (nssfCol) agg.nssf += parseMoney(row[nssfCol]);
        if (housingCol) agg.housing += parseMoney(row[housingCol]);
        if (pensionEmpCol) agg.pensionEmployer += parseMoney(row[pensionEmpCol]);
      }
      const out = Array.from(byEntity.values());
      out.forEach((r) => {
        r.otherAllow = Math.max(0, (r.gross || 0) - (r.basic || 0) - (r.rental || 0) - (r.commuter || 0));
        r.total = (r.gross || 0) + (r.nssf || 0) + (r.housing || 0) + (r.pensionEmployer || 0) + (r.nita || 0);
      });
      out.sort((a, b) => String(a.entity).localeCompare(String(b.entity)));
      out.forEach((r, i) => { r.seq = i + 1; });
      return out;
    },
    computeSpendingDetailRows(registerIndex) {
      const h = this.headers;
      const idCol = this.mapId || pickColumn(h, ["payrollnum", "idnum", "pf", "emp", "staff", "employee", "national"]);
      const nameCol = this.mapName || pickColumn(h, ["names", "employee name", "staff name", "full name", "name"]);
      const payrollNoCol = pickColumn(h, ["payrollnum", "payroll num", "pf no", "p/f no"]);
      const nationalIdCol = pickColumn(h, ["national id", "id no", "idnum", "nationalid"]);
      const basicCol = pickColumn(h, ["801 basic", "basic salary", "basic pay", "basic"]);
      const rentalCol = pickColumn(h, ["802 rental", "rental house all", "rental"]);
      const commuterCol = pickColumn(h, ["813 commuter", "commuter all", "commuter"]);
      const grossCol = pickColumn(h, ["grosspay", "gross pay", "gross salary", "gross"]);
      const nssfCol = pickColumn(h, ["933 nssf", "nssf auto", "nssf"]);
      const housingCol = pickColumn(h, ["944 housing", "housing levy fund", "housing levy"]);
      const pensionEmployerCol = pickColumn(h, ["pension (employer)", "pension employer"]);
      const pension902Col =
        pickColumn(h, ["902 pension", "902 pension (auto)", "pension (auto)"]) ||
        (h || []).find((x) => /^902\b/i.test(String(x))) ||
        null;
      const nitaCol = pickColumn(h, ["nita"]);
      const idx = registerIndex || this.registerIndex;
      const rows = this.csvRows.map((row) => {
        const rawId = idCol ? row[idCol] : null;
        const emp = rawId && idx ? idx.get(normId(rawId)) : null;
        const entity = emp
          ? String(emp.departmentName || "(No department)").trim()
          : "(Unknown — not in register)";
        return {
          entity,
          staffId: rawId || "",
          payrollNo: payrollNoCol ? (row[payrollNoCol] || "") : "",
          nationalId: nationalIdCol ? (row[nationalIdCol] || "") : "",
          name: nameCol ? (row[nameCol] || "") : "",
          basic: basicCol ? parseMoney(row[basicCol]) : 0,
          rental: rentalCol ? parseMoney(row[rentalCol]) : 0,
          commuter: commuterCol ? parseMoney(row[commuterCol]) : 0,
          gross: grossCol ? parseMoney(row[grossCol]) : 0,
          nssf: nssfCol ? parseMoney(row[nssfCol]) : 0,
          housing: housingCol ? parseMoney(row[housingCol]) : 0,
          pensionEmployer: pensionEmployerCol
            ? parseMoney(row[pensionEmployerCol])
            : (pension902Col ? parseMoney(row[pension902Col]) : 0),
          nita: nitaCol
            ? parseMoney(row[nitaCol])
            : (emp ? Number(this.nitaPerEmployee) || 0 : 0)
        };
      });
      rows.forEach((r) => {
        r.otherAllow = Math.max(0, (Number(r.gross) || 0) - (Number(r.basic) || 0) - (Number(r.rental) || 0) - (Number(r.commuter) || 0));
        r.projectedIncrement = (Number(r.basic) || 0) * 0.04;
        r.total = (Number(r.gross) || 0) + (Number(r.nssf) || 0) + (Number(r.housing) || 0) + (Number(r.pensionEmployer) || 0) + (Number(r.nita) || 0);
      });
      rows.sort((a, b) => String(a.entity).localeCompare(String(b.entity)));
      return rows;
    },
    downloadSampleCsv() {
      const lines = [
        "P/F No,Employee Name,Department,Basic Pay,Gross Pay,Net Pay",
        "PF001,Jane Doe,Finance,50000,75000,65000",
        "PF002,John Smith,Health,80000,120000,95000"
      ];
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "sample_payroll_reconciliation.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    },
    async fetchAllEmployees() {
      const all = [];
      let page = 1;
      const rows_per_page = 5000;
      let totalItems = null;
      /* eslint-disable no-await-in-loop */
      while (true) {
        const data = new FormData();
        data.append("function", "getEmployees");
        data.append("page", String(page));
        data.append("rows_per_page", String(rows_per_page));
        const res = await execute(data, authUrl);
        if (!res.data || !res.data.success) {
          throw new Error(res.data?.message || "Failed to load employees");
        }
        const payload = res.data.data || {};
        const batch = Array.isArray(payload.employees) ? payload.employees : [];
        all.push(...batch);
        totalItems = payload.totalItems != null ? Number(payload.totalItems) : all.length;
        if (batch.length === 0 || all.length >= totalItems) break;
        page += 1;
        if (page > 500) break;
      }
      return all;
    },
    filterRegister(list) {
      if (this.registerScope === "all") return list;
      return list.filter((e) => {
        const s = (e.status || "").toString().toLowerCase();
        return s === "active" || s === "";
      });
    },
    async runAnalysis() {
      this.errorMsg = "";
      if (!this.csvRows.length) {
        this.errorMsg = "Choose a payroll file (Excel or CSV) first.";
        return;
      }
      await this.loadPeriodStatus();
      if (this.periodFinalized) {
        this.errorMsg = "This month is finalized. Super Admin must reopen before analysis.";
        return;
      }
      if (this.canManage && this.nitaPerEmployee > 0 && (!this.preValidation || !this.preValidation.checks?.nita)) {
        await this.saveNitaConfig();
      }
      const valData = new FormData();
      valData.append("function", "validatePayrollReconPreAnalysis");
      valData.append("periodYear", String(this.periodYear));
      valData.append("periodMonth", String(this.periodMonth));
      const valRes = await execute(valData, authUrl);
      const validation = valRes.data?.data?.validation;
      this.preValidation = validation || this.preValidation;
      if (!validation || !validation.ready) {
        this.errorMsg = (validation?.errors || ["Pre-analysis requirements not met."]).join(" ");
        return;
      }

      const { idCol, payCol } = this.resolvedMaps();
      if (!idCol) {
        this.errorMsg = "Could not detect a staff / employee ID column.";
        return;
      }
      if (!payCol) {
        this.errorMsg = "Could not detect a pay column.";
        return;
      }

      this.loading = true;
      try {
        const raw = await this.fetchAllEmployees();
        const register = this.filterRegister(raw);
        this.registerIndex = buildRegisterIndex(register);

        const analysis = runRegisterPayrollAnalysis({
          csvRows: this.csvRows,
          headers: this.headers,
          registerList: register,
          columnMaps: {
            mapId: this.mapId,
            mapDept: this.mapDept,
            mapPay: this.mapPay,
            mapName: this.mapName
          },
          nitaPerEmployee: this.nitaPerEmployee
        });

        this.summary = analysis.summary;
        this.financialSummary = analysis.financialSummary;
        this.wageBillRows = analysis.wageBillRows;
        this.registerOnlyRows = analysis.registerOnlyRows;
        this.csvOnlyRows = analysis.csvOnlyRows;
        this.matchedDetailRows = analysis.matchedDetailRows;
        this.deptVariances = analysis.deptVariances;
        this.designationVariances = analysis.designationVariances;
        this.jobGroupVariances = analysis.jobGroupVariances;
        this.duplicateRows = analysis.duplicateRows;
        this.inactivePaidRows = analysis.inactivePaidRows;
        this.detectedEarnings = analysis.earnings;
        this.detectedDeductions = analysis.deductions;
        this.spendingSummaryRows = this.enrichSpendingSummaryFromLedger(analysis.spendingSummaryRows);
        this.spendingDetailRows = this.computeSpendingDetailRows(this.registerIndex);

        this.pensionRecon = reconcilePension({
          payrollMatchedRows: analysis.matchedDetailRows,
          pensionFileRowsList: this.pensionRows,
          pensionMapsList: null
        });
        this.gratuityRecon = reconcileGratuity({
          gratuityRows: this.gratuityRows,
          headers: this.gratuityHeaders,
          registerList: register,
          payrollMatchedRows: analysis.matchedDetailRows
        });
        this.executiveSummary = buildExecutiveSummary({
          financialSummary: analysis.financialSummary,
          pensionRecon: this.pensionRecon,
          gratuityRecon: this.gratuityRecon,
          spendingSummaryRows: this.spendingSummaryRows
        });

        await this.saveTemplateCatalog(analysis.codesForCatalog);

        this.analysisDone = true;
        this.activeTab = "executive";
      } catch (e) {
        this.errorMsg = e.message || String(e);
      } finally {
        this.loading = false;
      }
    },
    exportPdfSummary() {
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text(`Payroll vs Register — ${this.periodLabel}`, 14, 16);
      doc.setFontSize(10);
      doc.text(`Matched: ${this.summary.matchedCount} | Missing: ${this.summary.registerOnlyCount} | Extra: ${this.summary.csvOnlyCount}`, 14, 24);
      if (this.executiveSummary) {
        doc.text(`Payroll: ${this.formatMoney(this.executiveSummary.totalPayrollCost)} | Pension: ${this.formatMoney(this.executiveSummary.totalPensionCost)}`, 14, 30);
        doc.text(`Gratuity: ${this.formatMoney(this.executiveSummary.totalGratuityCost)} | NITA: ${this.formatMoney(this.executiveSummary.totalNitaCost)}`, 14, 36);
      }
      const body = (this.spendingSummaryRows || []).slice(0, 40).map((r) => [
        r.entity,
        String(r.staffCount),
        this.formatMoney(r.gross),
        this.formatMoney(r.pension902 || 0),
        this.formatMoney(r.nita)
      ]);
      autoTable(doc, {
        startY: 42,
        head: [["Department", "Staff", "Gross", "Pension 902", "NITA"]],
        body
      });
      doc.save(`payroll_recon_${this.periodYear}_${this.periodMonth}.pdf`);
    },
    exportExcelWorkbook() {
      const wb = XLSX.utils.book_new();
      const exec = this.executiveSummary || {};
      const summarySheet = XLSX.utils.aoa_to_sheet([
        ["Payroll vs Register", this.periodLabel],
        ["Matched", this.summary.matchedCount],
        ["Missing from payroll", this.summary.registerOnlyCount],
        ["Extra on payroll", this.summary.csvOnlyCount],
        ["Total payroll cost", exec.totalPayrollCost || this.financialSummary?.grossPay || 0],
        ["Pension (902)", exec.totalPensionCost || 0],
        ["Gratuity", exec.totalGratuityCost || 0],
        ["NITA", exec.totalNitaCost || 0],
        ["Total variance", exec.totalVariance || 0]
      ]);
      XLSX.utils.book_append_sheet(wb, summarySheet, "Executive");
      if (this.spendingSummaryRows.length) {
        const spendingAoA = [
          ["Department", "Staff", "Gross", "Pension 902", "NITA", "Total"],
          ...this.spendingSummaryRows.map((r) => [
            r.entity,
            r.staffCount,
            r.gross,
            r.pension902 || r.pensionEmployer || 0,
            r.nita,
            (Number(r.gross) || 0) + (Number(r.pension902 || r.pensionEmployer) || 0) + (Number(r.nita) || 0)
          ])
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(spendingAoA), "Spending");
      }
      if (this.pensionRecon && Array.isArray(this.pensionRecon.rows)) {
        const penAoA = [
          ["Emp No", "Name", "Payroll 902", "Pension 902", "Variance"],
          ...this.pensionRecon.rows.slice(0, 5000).map((r) => [
            r.empNo,
            r.name,
            r.payroll902,
            r.pension902,
            r.variance
          ])
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(penAoA), "Pension902");
      }
      XLSX.writeFile(wb, `payroll_recon_${this.periodYear}_${this.periodMonth}.xlsx`);
    },
    clearAll() {
      this.csvRows = [];
      this.headers = [];
      this.analysisDone = false;
      this.errorMsg = "";
      this.saveReportMessage = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      this.mapId = this.mapDept = this.mapPay = this.mapName = "";
      this.spendingSummaryRows = [];
      this.spendingDetailRows = [];
      this.wageBillRows = [];
      this.registerOnlyRows = [];
      this.csvOnlyRows = [];
      this.matchedDetailRows = [];
      this.pensionRows = { '16': [], '32': [], '33': [], '101': [] };
      this.pensionHeaders = { '16': [], '32': [], '33': [], '101': [] };
      this.gratuityRows = [];
      this.gratuityHeaders = [];
      this.uploadStatus = {};
      this.pensionRecon = null;
      this.gratuityRecon = null;
      this.executiveSummary = null;
      this.activeTab = "saved";
    },
    exportSummaryCsv() {
      const lines = [
        "Metric,Value",
        `CSV rows,${this.summary.csvRowCount}`,
        `Register employees (scope),${this.summary.registerCount}`,
        `Matched,${this.summary.matchedCount}`,
        `In CSV not in register,${this.summary.csvOnlyCount}`,
        `In register not in CSV,${this.summary.registerOnlyCount}`,
        `Total pay (CSV),${this.summary.totalCsvPay}`
      ];
      this.downloadBlob(lines.join("\n"), "payroll_reconciliation_summary.csv");
    },
    exportTableCsv(kind) {
      if (kind === "spending") {
        const hdr = [
          "S.No.",
          "County spending entity",
          "Staff earning",
          "Basic pay",
          "Rental house all.",
          "Commuter all.",
          "Other allowances",
          "Gross salary",
          "NSSF",
          "Housing levy",
          "Pension (employer)",
          "NITA",
          "Total"
        ];
        const body = this.spendingSummaryRows.map((r) =>
          [
            r.seq,
            this.csvEscape(r.entity),
            r.staffCount,
            r.basic,
            r.rental,
            r.commuter,
            r.otherAllow,
            r.gross,
            r.nssf,
            r.housing,
            r.pensionEmployer,
            r.nita,
            r.total
          ].join(",")
        );
        const t = this.spendingTotals;
        const totalLine = [
          "",
          this.csvEscape("County wage bill (total)"),
          t.staffCount,
          t.basic,
          t.rental,
          t.commuter,
          t.otherAllow,
          t.gross,
          t.nssf,
          t.housing,
          t.pensionEmployer,
          t.nita,
          t.total
        ].join(",");
        const lines = [hdr.join(",")].concat(body).concat(this.spendingSummaryRows.length ? [totalLine] : []);
        this.downloadBlob(lines.join("\n"), "SPENDING_ENTITY_COSTINGS_SUMMARY.csv");
      }
      if (kind === "wagebill") {
        const h = ["Department", "Headcount", "TotalPay"];
        const lines = [h.join(",")].concat(
          this.wageBillRows.map((r) =>
            [this.csvEscape(r.dept), r.count, r.total].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "department_wage_bill_from_csv.csv");
      }
      if (kind === "spendingDetail") {
        const h = ["S. No.", "PAYROLL No.", "ID. No.", "NAME", "COUNTY SPENDING ENTITY", "Basic Pay", "Rental House All.", "Commuter All.", "Other Allowances", "GrossPay", "Projected Annual Increment (4% of Basic Pay)", "NSSF", "H/Levy", "Pension (Employer)", "NITA"];
        const list = this.filteredSpendingDetailRows;
        const lines = [h.join(",")].concat(
          list.map((r, i) =>
            [
              i + 1,
              this.csvEscape(r.payrollNo),
              this.csvEscape(r.nationalId),
              this.csvEscape(r.name),
              this.csvEscape(r.entity),
              r.basic, r.rental, r.commuter, r.otherAllow, r.gross, r.projectedIncrement, r.nssf, r.housing, r.pensionEmployer, r.nita
            ].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "SPENDING_ENTITY_DETAILED_WAGEBILL.csv");
      }
      if (kind === "registerOnly") {
        const h = ["EmpNo", "NationalId", "Name", "Department", "Status"];
        const lines = [h.join(",")].concat(
          this.registerOnlyRows.map((e) =>
            [
              this.csvEscape(e.empNo),
              this.csvEscape(e.nationalId),
              this.csvEscape(this.fullName(e)),
              this.csvEscape(e.departmentName || e.departmentID),
              this.csvEscape(e.status)
            ].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "register_not_in_csv.csv");
      }
      if (kind === "csvOnly") {
        const h = ["StaffId", "Name", "Department", "Pay"];
        const lines = [h.join(",")].concat(
          this.csvOnlyRows.map((r) =>
            [this.csvEscape(r.staffId), this.csvEscape(r.name), this.csvEscape(r.dept), r.pay].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "csv_not_in_register.csv");
      }
      if (kind === "matched") {
        const h = ["EmpNo", "RegisterName", "RegisterDept", "CsvDept", "DeptMismatch", "Pay"];
        const lines = [h.join(",")].concat(
          this.matchedDetailRows.map((r) =>
            [
              this.csvEscape(r.empNo),
              this.csvEscape(r.regName),
              this.csvEscape(r.regDept),
              this.csvEscape(r.csvDept),
              r.deptMismatch ? "Yes" : "",
              r.pay
            ].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "payroll_csv_matched.csv");
      }
      if (kind === "pension") {
        const h = ["EmpNo", "Name", "Department", "Payroll902", "PensionFile902", "Variance", "Status"];
        const lines = [h.join(",")].concat(
          ((this.pensionRecon && this.pensionRecon.variances) || []).map((r) =>
            [this.csvEscape(r.empNo), this.csvEscape(r.name), this.csvEscape(r.department), r.payrollPension, r.pensionFile, r.variance, r.status].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "pension_reconciliation_902.csv");
      }
      if (kind === "gratuity") {
        const h = ["ID", "Name", "Department", "Amount", "InRegister", "InPayroll"];
        const lines = [h.join(",")].concat(
          ((this.gratuityRecon && this.gratuityRecon.uploadedRows) || []).map((r) =>
            [this.csvEscape(r.rawId), this.csvEscape(r.name), this.csvEscape(r.department), r.amount, r.inRegister ? "Yes" : "No", r.inPayroll ? "Yes" : "No"].join(",")
          )
        );
        this.downloadBlob(lines.join("\n"), "gratuity_reconciliation.csv");
      }
    },
    csvEscape(v) {
      const s = v == null ? "" : String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    },
    downloadBlob(text, name) {
      const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
    }
  }
};
</script>

<style scoped>
.pcr-content {
  width: 100%;
  max-width: 100%;
}
.filters-card {
  background: var(--hr-card-bg, #fff);
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid var(--hr-border, #e5e7eb);
}
.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
  align-items: end;
}
.filter-group.span-full {
  grid-column: 1 / -1;
}
.filter-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}
.filter-input,
.filter-select {
  width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
}
.table-card {
  background: var(--hr-card-bg, #fff);
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid var(--hr-border, #e5e7eb);
}
.pcr-mapping {
  border-top: 1px solid #eee;
  padding-top: 1rem;
}
.mapping-grid {
  display: grid;
  gap: 0.75rem;
  max-width: 720px;
}
.mapping-row {
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 1rem;
  align-items: center;
  font-size: 0.875rem;
}
.pcr-stat {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
}
.pcr-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
}
.pcr-stat-label {
  font-size: 0.8rem;
  color: #6c757d;
}
.hr-mb-1 {
  margin-bottom: 0.25rem;
}
.hr-mb-2 {
  margin-bottom: 0.5rem;
}
.hr-mb-3 {
  margin-bottom: 1rem;
}
.hr-mt-1 {
  margin-top: 0.25rem;
}
.hr-mt-3 {
  margin-top: 1rem;
}
.hr-small {
  font-size: 0.875rem;
}
.hr-text-muted {
  color: #6c757d;
}
.hr-p-0 {
  padding: 0;
}
.upload-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}
.upload-block {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.validation-panel {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  background: #f8fafc;
}
.validation-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.validation-list li {
  padding: 0.25rem 0;
  font-size: 0.875rem;
}
.validation-list li.ok::before {
  content: "✓ ";
  color: #198754;
}
.validation-list li.fail::before {
  content: "✗ ";
  color: #dc3545;
}
</style>
